import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ContactPicker from "../components/ContactPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreateListing = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedCategory = searchParams.get("category") || "";
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(true);

  const [form, setForm] = useState({
    name: "",
    category_id: preSelectedCategory,
    description: "",
    phone: "",
    email: user?.email || "",
    address: "",
    city: "",
    website: "",
    hours: "",
    image_url: "",
  });

  useEffect(() => {
    const init = async () => {
      // Fetch categories
      try {
        const catRes = await axios.get(`${API}/categories`);
        setCategories(catRes.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }

      setCheckingExisting(false);
    };

    init();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/services`, form, { withCredentials: true });
      toast.success("Your listing has been submitted for approval!");
      navigate("/provider");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create listing");
    } finally {
      setLoading(false);
    }
  };

  if (checkingExisting) {
    return (
      <div className="min-h-screen bg-[#F9F9F7]">
        <Header user={user} />
        <div className="pt-24 flex items-center justify-center h-[60vh]">
          <div className="w-12 h-12 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="create-listing-page">
      <Header user={user} />

      <main className="pt-24 lg:pt-28 pb-16 px-6 lg:px-12">
        <div className="max-w-2xl mx-auto">
          {/* Back */}
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#1A4D2E] mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 lg:p-8">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 bg-[#1A4D2E]/10 rounded-xl flex items-center justify-center">
                <Building2 className="w-7 h-7 text-[#1A4D2E]" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-[#1A1A1A]">List Your Trade</h1>
                <p className="text-[#71717A]">
                  Get discovered by contractors and property managers
                </p>
              </div>
              <ContactPicker 
                user={user} 
                categories={categories} 
                onContactAdded={() => navigate("/provider")}
                defaultAction="listing"
              />
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Business Name */}
              <div>
                <Label htmlFor="name" className="text-[#1A1A1A]">
                  Business / Trade Name *
                </Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Smith's Plumbing or John Doe Electric"
                  required
                  className="mt-1.5"
                  data-testid="name-input"
                />
              </div>

              {/* Category */}
              <div>
                <Label htmlFor="category" className="text-[#1A1A1A]">
                  Trade Category *
                </Label>
                <Select
                  value={form.category_id}
                  onValueChange={(value) => setForm({ ...form, category_id: value })}
                  required
                >
                  <SelectTrigger className="mt-1.5" data-testid="category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.category_id} value={cat.category_id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description" className="text-[#1A1A1A]">
                  Description *
                </Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Describe your services, experience, and what makes you stand out..."
                  required
                  className="mt-1.5 min-h-[120px]"
                  data-testid="description-input"
                />
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone" className="text-[#1A1A1A]">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    required
                    className="mt-1.5"
                    data-testid="phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-[#1A1A1A]">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="contact@yourbusiness.com"
                    required
                    className="mt-1.5"
                    data-testid="email-input"
                  />
                </div>
              </div>

              {/* Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="address" className="text-[#1A1A1A]">
                    Address *
                  </Label>
                  <Input
                    id="address"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="123 Main Street"
                    required
                    className="mt-1.5"
                    data-testid="address-input"
                  />
                </div>
                <div>
                  <Label htmlFor="city" className="text-[#1A1A1A]">
                    City *
                  </Label>
                  <Input
                    id="city"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="Your City"
                    required
                    className="mt-1.5"
                    data-testid="city-input"
                  />
                </div>
              </div>

              {/* Optional Fields */}
              <div className="border-t border-stone-200 pt-6">
                <p className="text-sm text-[#71717A] mb-4">Optional Information</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="website" className="text-[#1A1A1A]">
                      Website
                    </Label>
                    <Input
                      id="website"
                      type="url"
                      value={form.website}
                      onChange={(e) => setForm({ ...form, website: e.target.value })}
                      placeholder="https://yourbusiness.com"
                      className="mt-1.5"
                      data-testid="website-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="hours" className="text-[#1A1A1A]">
                      Business Hours
                    </Label>
                    <Input
                      id="hours"
                      value={form.hours}
                      onChange={(e) => setForm({ ...form, hours: e.target.value })}
                      placeholder="Mon-Fri 9am-5pm"
                      className="mt-1.5"
                      data-testid="hours-input"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Label htmlFor="image_url" className="text-[#1A1A1A]">
                    Cover Image URL
                  </Label>
                  <Input
                    id="image_url"
                    type="url"
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    placeholder="https://example.com/your-image.jpg"
                    className="mt-1.5"
                    data-testid="image-url-input"
                  />
                  <p className="text-xs text-[#71717A] mt-1">
                    Add a URL to an image that represents your business
                  </p>
                </div>
              </div>

              {/* Submit */}
              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={loading || !form.name || !form.category_id || !form.description || !form.phone || !form.email || !form.address || !form.city}
                  className="w-full bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full py-6 text-lg btn-hover-lift"
                  data-testid="submit-btn"
                >
                  {loading ? "Submitting..." : "Submit Listing for Review"}
                </Button>
                <p className="text-center text-sm text-[#71717A] mt-3">
                  Your listing will be reviewed before being published
                </p>
              </div>
            </form>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CreateListing;
